import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone.js'
import utc from 'dayjs/plugin/utc.js'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Creates a dayjs object from a time string in HH:mm:ss format
 * @param {string} timeString - Time string in HH:mm:ss format (e.g. "13:45:00")
 * @returns {dayjs.Dayjs} A dayjs object representing the time on Jan 1, 1970
 */
export const getDayjsFromTime = (time) => {
  return dayjs(`1970-01-01T${time}`)
}

/**
 * Formats a date string into YYYY-MM-DD format
 * @param {string} datetime - Date string or ISO datetime string
 * @returns {string} Formatted date string (e.g. "2025-02-13")
 */
export const formatToDate = (datetime) => {
  return dayjs(datetime).format('YYYY-MM-DD')
}

/**
 * Formats a date string into full date format (e.g. "Sunday, February 23, 2025")
 * @param {string} date - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date string
 */
export const formatFullDate = (date) => {
  return dayjs(date).format('dddd, MMMM D, YYYY')
}

/**
 * Formats an ISO datetime string into full date and time format
 * @param {string} datetime - ISO datetime string (e.g. "2025-03-25T03:00:00.000Z")
 * @param {boolean} [includeTime=true] - Whether to include time in the output
 * @returns {string} Formatted string (e.g. "Sunday, February 23, 2025 at 3:00 AM")
 */
export const formatFullDateTime = (datetime, includeTime = true) => {
  const date = dayjs(datetime)
  if (!includeTime) {
    return date.format('dddd, MMMM D, YYYY')
  }
  return date.format('h:mm A [on] dddd, MMMM D, YYYY')
}

/**
 * Formats a time string into HH:mm:ss format
 * @param {dayjs.Dayjs} time - A dayjs object representing the time on Jan 1, 1970
 * @returns {string} Formatted time string in HH:mm:ss format
 */
export const timeToHHMMSS = (time) => {
  return time?.format('HH:mm:ss')
}

/**
 * Formats a time string in HH:mm:ss format to a 12-hour format with AM/PM
 * @param {string} time - Time string in HH:mm:ss format (e.g. "13:45:00")
 * @returns {string} Formatted time string in HH:mm AM/PM format (e.g. "01:45 PM")
 */
export const formatTimeToAMPM = (time) => {
  return dayjs(`1970-01-01T${time}`).format('HH:mm A')
}

/**
 * Formats a time slot string in HH:mm:ss format to a 12-hour format with AM/PM
 * @param {string} startTime - Start time string in HH:mm:ss format (e.g. "13:45:00")
 * @param {string} endTime - End time string in HH:mm:ss format (e.g. "13:45:00")
 * @returns {string} Formatted time slot string in HH:mm AM/PM format (e.g. "01:45 PM - 02:45 PM")
 */
export const formatTimeSlot = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return
  }
  return `${dayjs(startTime).format('HH:mm')} - ${dayjs(endTime).format('HH:mm')}`
}

export const isSameSession = (sessionA, sessionB) => {
  const sessionAStartTime = dayjs(sessionA.startTime)
  const sessionAEndTime = dayjs(sessionA.endTime)
  const sessionBStartTime = dayjs(sessionB.startTime)
  const sessionBEndTime = dayjs(sessionB.endTime)

  return sessionAStartTime.isSame(sessionBStartTime, 'minute') && sessionAEndTime.isSame(sessionBEndTime, 'minute')
}

export const isOverlappingSession = (sessionA, sessionB) => {
  // Convert all times to dayjs objects for consistent comparison
  const sessionAStartTime = dayjs(sessionA.startTime)
  const sessionAEndTime = dayjs(sessionA.endTime)
  const sessionBStartTime = dayjs(sessionB.startTime)
  const sessionBEndTime = dayjs(sessionB.endTime)

  // Return false if there is overlap
  return sessionAStartTime.isBefore(sessionBEndTime, 'minute') && sessionAEndTime.isAfter(sessionBStartTime, 'minute')
}
